import mixpanel from 'mixpanel-browser';

import { SentryCaptureMessage } from '../../sentry';
import type { EventMetaData } from '../types';

const MIXPANEL_STAGING_TK = '2272344adf54e7921a0b0b0cbe4e7b8f';
const MIXPANEL_PRODUCTION_TK = '57ffe08cf93dd0923b6486a070843c0c';

// Set REACT_APP_MIXPANEL_LOCAL=true in .env.local (git-ignored) to enable
// tracking from local dev. Events route to the staging Mixpanel project via
// the staging token. Requires MIXPANEL_STAGING_TK to be set.
const isLocalTestingMixpanel =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_MIXPANEL_LOCAL === 'true';

const isStaging =
  window.location.hostname.includes('staging') || isLocalTestingMixpanel;
const isProd = process.env.NODE_ENV === 'production' && !isStaging;
const isTest = process.env.NODE_ENV === 'test';

// Mixpanel is switched off in every environment. Set to false to restore the
// environment-based behaviour below.
const IS_MIXPANEL_DISABLED = true;

const getIsMixpanelEnabled = (): boolean =>
  !IS_MIXPANEL_DISABLED && [isStaging, isProd, isTest].some(Boolean);

const getProjectToken = (): string =>
  isProd ? MIXPANEL_PRODUCTION_TK : MIXPANEL_STAGING_TK;

export const mixpanelSetConfig = (): void => {
  if (getIsMixpanelEnabled()) {
    try {
      mixpanel.set_config({
        api_host: 'https://api-eu.mixpanel.com',
      });
    } catch (e) {
      SentryCaptureMessage(
        `Mixpanel - Issue occurred when setting config: ${(e as Error).message}`,
        'error'
      );
    }
  }
};

export const mixpanelInit = (): void => {
  if (getIsMixpanelEnabled()) {
    try {
      mixpanel.init(getProjectToken(), {
        api_host: 'https://api-eu.mixpanel.com',
      });
    } catch (e) {
      SentryCaptureMessage(
        `Mixpanel - Issue occurred when initializing: ${(e as Error).message}`,
        'error'
      );
    }
  }
};

export const mixpanelTrackPageView = (): void => {
  if (getIsMixpanelEnabled()) {
    try {
      mixpanel.track_pageview();
    } catch (e) {
      SentryCaptureMessage(
        `Mixpanel - Issue occurred when tracking page view: ${(e as Error).message}`,
        'error'
      );
    }
  }
};

export const mixpanelTrack = (
  eventName: string,
  metaData?: EventMetaData
): void => {
  if (getIsMixpanelEnabled()) {
    try {
      mixpanel.track(eventName, metaData);
    } catch (e) {
      SentryCaptureMessage(
        `Mixpanel - Issue occurred when tracking event: ${(e as Error).message}`,
        'error'
      );
    }
  }
};
